/**
 * <EnonceReadingSheet> — la fiche « Ma lecture » : comment l'élève lit ses énoncés (template).
 *
 * Les gestes de l'ancienne fiche (`ReadingSheet`), sur le design system : peu d'options, la lecture
 * adaptée d'un coup (`<Switch>`), puis la police et la taille du texte (des pilules de sélection,
 * `<ChoiceChip variant="pill">`), et « C'est bon ». Une feuille basse sur un téléphone, une carte au
 * milieu sur un grand écran (`<Modal sizeToContent>`), comme les autres feuilles de l'élève.
 *
 * Contrôlée : la lecture montrée arrive, chaque geste repart tout de suite (l'énoncé change sous la
 * fiche). La fermeture, quelle qu'en soit la façon — « C'est bon », la croix, le fond, le retour —
 * passe par `onClose` : c'est là que l'écran enregistre.
 *
 * Les tailles et les polices sont celles que le serveur accepte (`StudentProfile::READING_SIZES`,
 * `READING_FONTS`) ; le mot « dys » n'apparaît jamais.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { type UIEnonceReading, type UIEnonceReadingFont } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Switch } from "../../atoms/Switch"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"
import { SettingRow } from "../../molecules/SettingRow"

const TEXTS = {
    title: "Ma lecture",
    adapted: "Lecture adaptée",
    adaptedHint: "Allume d'un coup des réglages pour lire plus facilement.",
    font: "Police",
    size: "Taille du texte",
    done: "C'est bon",
}

const FONTS: { value: UIEnonceReadingFont; label: string }[] = [
    { value: "standard", label: "Standard" },
    { value: "lexend", label: "Lexend" },
]

const SIZES: { value: number; label: string }[] = [
    { value: 16, label: "Normal" },
    { value: 18, label: "Grand" },
    { value: 21, label: "Très grand" },
]

export type EnonceReadingSheetProps = {
    visible: boolean
    /** La lecture montrée : celle du compte, ou celle que l'élève est en train de régler. */
    reading: UIEnonceReading
    /** L'interrupteur « Lecture adaptée » : l'écran pose l'un des deux préréglages. */
    onToggleAdapted: (adapted: boolean) => void
    /** Un réglage changé (police, taille) : la lecture complète. */
    onChange: (next: UIEnonceReading) => void
    onClose: () => void
    testID?: string
}

export function EnonceReadingSheet(props: EnonceReadingSheetProps) {
    const { visible, reading, onToggleAdapted, onChange, onClose, testID } = props

    return (
        <Modal visible={visible} onClose={onClose} title={TEXTS.title} sizeToContent testID={testID}>
            <Stack gap="4">
                <SettingRow
                    label={TEXTS.adapted}
                    description={TEXTS.adaptedHint}
                    trailing={
                        <Switch
                            checked={reading.adapted}
                            onChange={onToggleAdapted}
                            accessibilityLabel={TEXTS.adapted}
                        />
                    }
                />
                <Stack gap="2">
                    <Text size="text-base" weight="bold" color="text-strong">
                        {TEXTS.font}
                    </Text>
                    <Row gap="2" style={styles.choices}>
                        {FONTS.map((font) => (
                            <ChoiceChip
                                key={font.value}
                                variant="pill"
                                label={font.label}
                                selected={reading.font === font.value}
                                onPress={() => onChange({ ...reading, font: font.value })}
                            />
                        ))}
                    </Row>
                </Stack>
                <Stack gap="2">
                    <Text size="text-base" weight="bold" color="text-strong">
                        {TEXTS.size}
                    </Text>
                    <Row gap="2" style={styles.choices}>
                        {SIZES.map((size) => (
                            <ChoiceChip
                                key={size.value}
                                variant="pill"
                                label={size.label}
                                accessibilityLabel={`Texte ${size.label.toLowerCase()}`}
                                selected={reading.size === size.value}
                                onPress={() => onChange({ ...reading, size: size.value })}
                            />
                        ))}
                    </Row>
                </Stack>
                <Button label={TEXTS.done} onPress={onClose} fullWidth />
            </Stack>
        </Modal>
    )
}

const styles = StyleSheet.create(() => ({
    /** Les pilules passent à la ligne sur un téléphone étroit plutôt que de déborder. */
    choices: { flexWrap: "wrap" as const },
}))
